import { createRouter, createWebHashHistory } from 'vue-router'
import {
  Calendar,
  Collection,
  CreditCard,
  Dish,
  Download,
  Files,
  FolderOpened,
  Iphone,
  Notification,
  OfficeBuilding,
  Picture,
  Reading,
  Setting,
  Sort,
  TakeawayBox,
  Tickets,
  TrophyBase,
  User
} from '@element-plus/icons-vue'

import store from '../store'
import { isFeatureEnabled } from '../telemetry/featureFlags'
import Layout from '../views/layout/Layout'
import { authorizationGuard, loginPageGuard, startPageGuard } from './guards'

const routes = [
  {
    path: '/login',
    component: () => import('../views/login'),
    hidden: true,
    beforeEnter: loginPageGuard
  },
  { path: '/404', component: () => import('../views/errorPage/404'), hidden: true },
  { path: '/401', component: () => import('../views/errorPage/401'), hidden: true },
  {
    path: '/',
    component: Layout,
    redirect: startPageGuard,
    sidebarElements: true,
    children: [
      {
        path: '/dashboard',
        component: () => import('../views/dashboard'),
        name: 'dashboard',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          icon: OfficeBuilding,
          title: 'dashboard',
          noCache: true
        }
      },
      {
        path: '/selective-notifications',
        component: () => import('../views/selective-notifications'),
        name: 'selectiveNotifications',
        meta: {
          roles: ['EDITOR_NOTIFICATION', 'ADMIN', 'ROOT'],
          icon: Notification,
          title: 'selectiveNotifications'
        }
      },
      {
        path: '/xmloffers',
        component: () => import('../views/XMLOffers'),
        name: 'xmlOffers',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          icon: Sort,
          title: 'xmlOffers'
        }
      },
      {
        path: '/categories',
        component: () => import('../views/categories'),
        name: 'categories',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          title: 'categories',
          icon: Collection
        }
      },
      {
        path: '/product-hits',
        component: () => import('../views/productHits'),
        name: 'productHits',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          icon: TrophyBase,
          title: 'productHits',
          isEnabled: () => isFeatureEnabled('product-hits-feature')
        }
      },
      {
        path: '/inserts',
        component: () => import('../views/inserts'),
        name: 'inserts',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          icon: Tickets,
          title: 'inserts'
        }
      },
      {
        path: '/users',
        component: () => import('../views/users'),
        name: 'users',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          title: 'users',
          icon: User
        }
      },
      {
        path: '/groups',
        component: () => import('../views/groups'),
        name: 'groups',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          title: 'groups',
          icon: FolderOpened
        }
      },
      {
        path: '/archives',
        redirect: '/archives/leaflets',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR', 'EDITOR_NOTIFICATION'],
          title: 'archives',
          icon: Files
        },
        children: [
          {
            path: 'leaflets',
            component: () => import('../views/archives/leafletsArchive'),
            name: 'leafletsArchive',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR'],
              title: 'leafletsArchive',
              alternativeTitle: 'leafletsAlternative',
              id: 'leaflets',
              icon: TakeawayBox
            }
          },
          {
            path: 'shops',
            component: () => import('../views/archives/shopsArchive'),
            name: 'shopsArchive',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR'],
              title: 'shopsArchive',
              alternativeTitle: 'shopsAlternative',
              id: 'shops',
              icon: TakeawayBox
            }
          },
          {
            path: 'notifications',
            component: () => import('../views/archives/notificationsArchive'),
            name: 'notificationsArchive',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR', 'EDITOR_NOTIFICATION'],
              title: 'notificationsArchive',
              alternativeTitle: 'notificationsAlternative',
              id: 'notifications',
              icon: TakeawayBox
            }
          },
          {
            path: 'leaflet-details',
            component: () => import('../views/archives/leafletDetails'),
            name: 'leafletDetails',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR'],
              title: 'leafletDetails',
              alternativeTitle: 'leafletAlternative',
              id: 'leaflet-details',
              icon: TakeawayBox
            }
          },
          {
            path: 'inserts',
            component: () => import('../views/archives/InsertsArchive/InsertsArchive.vue'),
            name: 'insertsArchive',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR'],
              title: 'insertsArchive',
              alternativeTitle: 'insertsAlternative',
              id: 'inserts',
              icon: TakeawayBox
            }
          }
        ]
      },
      {
        path: '/logs',
        component: () => import('../views/logs'),
        name: 'logs',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          icon: Reading,
          title: 'logs'
        }
      },
      {
        path: '/recipes',
        component: () => import('../views/cooks-and-recipes'),
        name: 'CooksAndRecipes',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR_RECIPE'],
          title: 'cooksAndRecipes',
          icon: Dish
        }
      },
      {
        path: '/scrapper',
        name: 'Scrapper',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          title: 'scrapper',
          icon: Download
        },
        children: [
          {
            path: 'pdf',
            component: () => import('../views/scrapper/scrapper'),
            name: 'Scrapper',
            meta: {
              roles: ['ADMIN', 'ROOT'],
              title: 'scrapper',
              icon: Download,
              isEnabled: () => store.getters.leafletScrapperFeature
            }
          },
          {
            path: 'new-leaflets-search',
            component: () => import('../views/scrapper/new-leaflets-search'),
            name: 'newLeafletsSearch',
            meta: {
              roles: ['ADMIN', 'ROOT', 'EDITOR'],
              title: 'scrapperNewLeafletsSearch',
              id: 'scrapperNewLeafletsSearch',
              alternativeTitle: 'scrapperNewLeafletsSearchAlternative',
              icon: Download,
              isEnabled: () => import.meta.env.VITE_SCRAPPER_LEAFLET_SEARCH_FEATURE
            }
          }
        ]
      },
      {
        path: '/reels',
        component: () => import('../views/reels'),
        name: 'Reels',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR_REELS'],
          title: 'reels',
          icon: Iphone
        }
      },
      {
        path: '/loyalty-cards',
        component: () => import('../views/loyalty-cards'),
        name: 'LoyaltyCards',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          title: 'loyaltyCards',
          icon: CreditCard
        }
      },
      {
        path: '/app-config',
        component: () => import('../views/app-config'),
        name: 'AppConfig',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          title: 'appConfig',
          icon: Setting
        }
      },
      {
        path: '/slider',
        component: () => import('../views/slider'),
        name: 'Slider',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          title: 'slider',
          icon: Picture
        }
      },
      {
        path: '/trade-days',
        component: () => import('../views/trade-days'),
        name: 'TradeDays',
        meta: {
          roles: ['ADMIN', 'ROOT'],
          title: 'tradeDays',
          icon: Calendar,
          isEnabled: () => import.meta.env.VITE_TRADE_DAYS_FEATURE
        }
      },
      {
        hidden: true,
        path: '/shops/leaflets/:storeId/pageDetails/:leafletId/:leafletNaturalId',
        component: () => import('../views/leafletPageDetails'),
        name: 'pageDetails',
        meta: {
          roles: ['ADMIN', 'ROOT', 'EDITOR'],
          title: 'pageDetails'
        }
      },
      {
        hidden: true,
        path: '/password',
        component: () => import('../views/password'),
        name: 'password',
        meta: {
          title: 'password'
        }
      },
      {
        hidden: true,
        path: '/:pathMatch(.*)*',
        redirect: '/404'
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    if (!savedPosition && to.hash) {
      return false
    }

    return new Promise(resolve => {
      setTimeout(() => {
        if (savedPosition) {
          return resolve(savedPosition)
        }

        return resolve({ top: 0, left: 0 })
      }, 280 + 80)
    })
  }
})

router.beforeEach(authorizationGuard)

export default router
export { routes }
